/* Theme picker — switch the visual identity of AI Board at runtime.
 *
 * Four themes ship today (see src/styles/tokens.css for the palettes):
 *   - matrix     : default. Green-on-ink, code-rain backdrop.
 *   - control    : Agente 86 spy aesthetic. Phone-red + brass + manila.
 *   - editorial  : NYT-style cream + ink. Calm reading mode.
 *   - boardroom  : Investor-grade dark navy + ice blue + gold.
 *
 * Persistence: lp_aiboard_theme in localStorage. The choice is loaded
 * BEFORE first paint via a tiny inline script in each HTML head so we
 * never flash the wrong theme (FOUC).
 *
 * Switching is gestural — clicking a card in the panel applies the
 * theme with a 400ms cross-fade (driven by `html { transition: ...; }`
 * in tokens.css) and persists.
 */

import { toast } from './toast';
import { safeText } from './safe-dom';

export type ThemeId = 'matrix' | 'control' | 'editorial' | 'boardroom';

export interface ThemeDef {
  id: ThemeId;
  label: string;
  tagline: string;
  /** Three swatch colors for the preview card: ink / matrix / accent. */
  swatch: [string, string, string];
}

export const THEMES: readonly ThemeDef[] = [
  {
    id: 'matrix',
    label: 'Matrix',
    tagline: 'Default — green-on-ink, code rain. The Oracle speaks.',
    swatch: ['#0B1120', '#00FF9C', '#F38281'],
  },
  {
    id: 'control',
    label: 'CONTROL',
    tagline: 'Agente 86 — phone-booth red, brass, manila stamp.',
    swatch: ['#0A1832', '#C41E3A', '#D4A857'],
  },
  {
    id: 'editorial',
    label: 'Editorial',
    tagline: 'Cream paper, ink red. Read in peace.',
    swatch: ['#F5F1E8', '#A41E22', '#0A0A0A'],
  },
  {
    id: 'boardroom',
    label: 'Boardroom',
    tagline: 'Deep navy, ice blue, gold. Investor mode.',
    swatch: ['#0F1B2D', '#5DADE2', '#D4A857'],
  },
] as const;

const STORAGE_KEY = 'lp_aiboard_theme';
const DEFAULT_THEME: ThemeId = 'matrix';

function isValidTheme(s: string | null): s is ThemeId {
  return s != null && THEMES.some((t) => t.id === s);
}

export function getCurrentTheme(): ThemeId {
  const saved = localStorage.getItem(STORAGE_KEY);
  return isValidTheme(saved) ? saved : DEFAULT_THEME;
}

export function applyTheme(id: ThemeId, persist = true): void {
  document.documentElement.setAttribute('data-theme', id);
  if (persist) localStorage.setItem(STORAGE_KEY, id);
  window.dispatchEvent(new CustomEvent('lp-theme-changed', { detail: { theme: id } }));
}

// ============================================================================
// Picker panel — slides in from the right edge.
// ============================================================================

const PANEL_ID = 'theme-picker-panel';

function buildPanel(): HTMLElement {
  let panel = document.getElementById(PANEL_ID);
  if (panel) return panel;

  panel = document.createElement('aside');
  panel.id = PANEL_ID;
  panel.className = 'theme-picker';
  panel.setAttribute('aria-hidden', 'true');
  panel.setAttribute('role', 'dialog');
  panel.setAttribute('aria-label', 'Theme picker');

  panel.innerHTML = `
    <div class="theme-picker-backdrop" data-close="1"></div>
    <div class="theme-picker-card" role="document">
      <header class="theme-picker-head">
        <div>
          <span class="theme-picker-eyebrow">LOOK &amp; FEEL</span>
          <h2 class="theme-picker-title">Choose your theme</h2>
          <p class="theme-picker-sub">Your choice persists in this browser. Switch any time.</p>
        </div>
        <button type="button" class="theme-picker-close" data-close="1" aria-label="Close">
          <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor"
               stroke-width="1.7" stroke-linecap="round">
            <path d="M3 3l10 10M13 3L3 13"/>
          </svg>
        </button>
      </header>
      <div class="theme-grid" role="radiogroup">
        ${THEMES.map((t) => themeCardHtml(t)).join('')}
      </div>
      <footer class="theme-picker-foot">
        <span class="theme-picker-hint">Press Esc to close.</span>
      </footer>
    </div>
  `;
  document.body.appendChild(panel);

  // Backdrop + close button
  panel.addEventListener('click', (ev) => {
    const t = ev.target as HTMLElement;
    if (t.dataset.close === '1' || (t.closest('[data-close]') as HTMLElement)?.dataset.close === '1') {
      closePanel();
    }
  });

  // Card click → apply theme
  panel.querySelectorAll<HTMLButtonElement>('[data-theme-id]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = btn.dataset.themeId as ThemeId;
      applyTheme(id);
      refreshActiveState();
      // Toast confirmation — stay open so they can A/B compare.
      const def = THEMES.find((t) => t.id === id)!;
      toast(`Theme: ${def.label}`);
    });
  });

  // Esc closes (single document-level listener guard)
  if (!(window as any).__themePickerEscBound) {
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        const p = document.getElementById(PANEL_ID);
        if (p && p.getAttribute('aria-hidden') === 'false') closePanel();
      }
    });
    (window as any).__themePickerEscBound = true;
  }

  return panel;
}

function themeCardHtml(t: ThemeDef): string {
  return `
    <button type="button" class="theme-card" data-theme-id="${t.id}" role="radio" aria-checked="false">
      <div class="theme-card-swatches" aria-hidden="true">
        <span style="background:${t.swatch[0]}"></span>
        <span style="background:${t.swatch[1]}"></span>
        <span style="background:${t.swatch[2]}"></span>
      </div>
      <div class="theme-card-meta">
        <div class="theme-card-label">${t.label}</div>
        <div class="theme-card-tagline"></div>
        <span class="theme-card-check" aria-hidden="true">
          <svg viewBox="0 0 16 16" width="10" height="10" fill="none" stroke="currentColor"
               stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M3 8l3 3 7-7"/>
          </svg>
        </span>
      </div>
    </button>
  `;
}

function refreshActiveState(): void {
  const current = getCurrentTheme();
  const cards = document.querySelectorAll<HTMLButtonElement>('[data-theme-id]');
  cards.forEach((c) => {
    const on = c.dataset.themeId === current;
    c.classList.toggle('theme-card-on', on);
    c.setAttribute('aria-checked', String(on));
    // Fill the tagline text via textContent (XSS-safe).
    const tag = c.querySelector('.theme-card-tagline') as HTMLElement | null;
    if (tag) {
      const def = THEMES.find((t) => t.id === c.dataset.themeId);
      if (def) safeText(tag, def.tagline);
    }
  });
}

export function openThemePicker(): void {
  const p = buildPanel();
  refreshActiveState();
  p.setAttribute('aria-hidden', 'false');
  // Trap focus on the first card for keyboard users.
  const firstCard = p.querySelector<HTMLButtonElement>('[data-theme-id]');
  setTimeout(() => firstCard?.focus(), 50);
}

function closePanel(): void {
  const p = document.getElementById(PANEL_ID);
  if (p) p.setAttribute('aria-hidden', 'true');
}

// ============================================================================
// Boot
// ============================================================================

/** Apply the persisted theme immediately. Safe to call at module top — it
 *  reads localStorage and sets the attribute synchronously, before
 *  attachThemePickerHandlers runs. Designed to be called from main.ts at
 *  very top of boot so the picker DOM is themed correctly. */
export function applyPersistedTheme(): void {
  applyTheme(getCurrentTheme(), /*persist*/ false);
}

export function attachThemePickerHandlers(): void {
  // Header button — opens the panel.
  document.getElementById('theme-picker-btn')?.addEventListener('click', (e) => {
    e.preventDefault();
    openThemePicker();
  });
}

// Console + window surface for debugging / programmatic use.
if (typeof window !== 'undefined') {
  (window as any).lpThemes = THEMES;
  (window as any).lpApplyTheme = applyTheme;
  (window as any).lpOpenThemePicker = openThemePicker;
}
